import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as alertsApi from '../api/alerts';
import * as sitesApi from '../api/sites';

export function Alerts() {
  const { selectedOrg } = useOrg();
  const { canReadAssets, canWriteAssets } = usePermissions();
  const [alerts, setAlerts] = useState([]);
  const [sites, setSites] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAckModal, setShowAckModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [ackNotes, setAckNotes] = useState('');
  const [acknowledging, setAcknowledging] = useState(false);
  const [filters, setFilters] = useState({
    siteId: '',
    assetId: '',
    severity: '',
    acknowledged: '',
    from: '',
    to: ''
  });

  const loadSites = useCallback(async () => {
    if (!selectedOrg) return;
    try {
      const sitesList = await sitesApi.getSites(selectedOrg.id);
      setSites(sitesList);
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  }, [selectedOrg]);

  const loadAlerts = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await alertsApi.getAlerts(selectedOrg.id, {
        siteId: filters.siteId || undefined,
        assetId: filters.assetId || undefined,
        severity: filters.severity || undefined,
        acknowledged: filters.acknowledged !== '' ? filters.acknowledged : undefined,
        from: filters.from || undefined,
        to: filters.to || undefined
      });
      setAlerts(result.alerts);
      setTotal(result.total);
      setLastUpdated(result.lastUpdated);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, filters]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleOpenAckModal = (alert) => {
    setSelectedAlert(alert);
    setAckNotes('');
    setShowAckModal(true);
  };

  const handleAcknowledge = async () => {
    if (!selectedOrg || !selectedAlert || acknowledging) return;
    setAcknowledging(true);
    try {
      await alertsApi.acknowledgeAlert(selectedOrg.id, selectedAlert.id, ackNotes);
      setShowAckModal(false);
      loadAlerts();
    } catch (err) {
      setError(err.message);
    } finally {
      setAcknowledging(false);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view alerts.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Alerts"
        subtitle={`${total} alerts for ${selectedOrg?.name}`}
      />

      <div style={styles.toolbar}>
        <div style={styles.filters}>
          <select
            value={filters.siteId}
            onChange={(e) => handleFilterChange('siteId', e.target.value)}
            style={styles.select}
            data-testid="alerts-site-filter"
          >
            <option value="">All Sites</option>
            {sites.map(site => (
              <option key={site.id} value={site.id}>{site.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Asset ID"
            value={filters.assetId}
            onChange={(e) => handleFilterChange('assetId', e.target.value)}
            style={styles.input}
            data-testid="alerts-asset-filter"
          />
          <select
            value={filters.severity}
            onChange={(e) => handleFilterChange('severity', e.target.value)}
            style={styles.select}
            data-testid="alerts-severity-filter"
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
          <select
            value={filters.acknowledged}
            onChange={(e) => handleFilterChange('acknowledged', e.target.value)}
            style={styles.select}
            data-testid="alerts-ack-filter"
          >
            <option value="">All States</option>
            <option value="false">Unacknowledged</option>
            <option value="true">Acknowledged</option>
          </select>
          <input
            type="date"
            value={filters.from}
            onChange={(e) => handleFilterChange('from', e.target.value)}
            style={styles.input}
            data-testid="alerts-from-filter"
          />
          <input
            type="date"
            value={filters.to}
            onChange={(e) => handleFilterChange('to', e.target.value)}
            style={styles.input}
            data-testid="alerts-to-filter"
          />
        </div>
        {lastUpdated && (
          <span style={styles.lastUpdated}>
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </span>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading alerts...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="alerts-table">
            <thead>
              <tr>
                <th style={styles.th}>Severity</th>
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Site</th>
                <th style={styles.th}>Asset</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map(alert => (
                <tr key={alert.id} style={styles.tr} data-testid={`alert-${alert.id}`}>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.severityBadge,
                      background: getSeverityColor(alert.severity)
                    }}>
                      {alert.severity}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.alertTitle}>{alert.title}</div>
                    <div style={styles.alertMessage}>{alert.message}</div>
                  </td>
                  <td style={styles.td}>{alert.siteName}</td>
                  <td style={styles.td}>{alert.assetName}</td>
                  <td style={styles.td}>{new Date(alert.createdAt).toLocaleString()}</td>
                  <td style={styles.td}>
                    {alert.acknowledged ? (
                      <span style={styles.ackBadge}>
                        Acknowledged
                        {alert.acknowledgedNotes && (
                          <span style={styles.ackNotes}> - {alert.acknowledgedNotes}</span>
                        )}
                      </span>
                    ) : (
                      <span style={styles.unackBadge}>Unacknowledged</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {!alert.acknowledged && canWriteAssets && (
                      <button
                        onClick={() => handleOpenAckModal(alert)}
                        style={styles.ackButton}
                        data-testid={`ack-${alert.id}`}
                      >
                        Acknowledge
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {alerts.length === 0 && (
                <tr>
                  <td colSpan="7" style={styles.emptyState}>
                    No alerts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAckModal && selectedAlert && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="ack-modal">
            <h3 style={styles.modalTitle}>Acknowledge Alert</h3>
            <p style={styles.modalSubtitle}>{selectedAlert.title}</p>
            <div style={styles.formGroup}>
              <label style={styles.label}>Notes (optional)</label>
              <textarea
                value={ackNotes}
                onChange={(e) => setAckNotes(e.target.value)}
                style={styles.textarea}
                placeholder="Add notes about this acknowledgement..."
                data-testid="ack-notes-input"
              />
            </div>
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowAckModal(false)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleAcknowledge}
                disabled={acknowledging}
                style={styles.submitButton}
                data-testid="confirm-ack-button"
              >
                {acknowledging ? 'Acknowledging...' : 'Acknowledge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getSeverityColor(severity) {
  const colors = {
    critical: '#fc8181',
    warning: '#f6e05e',
    info: '#63b3ed'
  };
  return colors[severity] || '#e2e8f0';
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  filters: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  input: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  lastUpdated: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568',
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem',
    verticalAlign: 'top'
  },
  severityBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  alertTitle: {
    fontWeight: '500',
    marginBottom: '0.25rem'
  },
  alertMessage: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  ackBadge: {
    color: '#276749',
    fontSize: '0.75rem'
  },
  ackNotes: {
    fontStyle: 'italic'
  },
  unackBadge: {
    color: '#c53030',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  ackButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.75rem',
    cursor: 'pointer'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '400px',
    maxWidth: '500px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  modalTitle: {
    margin: '0 0 0.5rem 0',
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  modalSubtitle: {
    margin: '0 0 1.5rem 0',
    color: '#718096',
    fontSize: '0.875rem'
  },
  formGroup: {
    marginBottom: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#4a5568'
  },
  textarea: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    minHeight: '80px',
    resize: 'vertical'
  },
  modalActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem'
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  }
};
